import { useMutation, useQuery } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { useApi } from '@/shared/api/useApi';
import { useAuth } from '@/shared/auth';
import { Button, PageHeader } from '@/shared/components';
import { PluginProblem } from './PluginProblem';
import { usePluginCatalogue } from './usePluginCatalogue';

export function PluginConnectPage() {
  const { linkId, nonce, state } = useSearch({ from: '/plugins/connect' });
  const api = useApi();
  const { user } = useAuth();
  const proof = { nonce, state };
  const catalogue = usePluginCatalogue();
  // Proofs stay in the URL and request body; never in persisted query keys or browser storage.
  const preview = useQuery({
    queryKey: ['plugin-link', linkId],
    queryFn: () => api.plugins.previewLink(linkId, proof),
    enabled: !!linkId && !!nonce && !!state,
    retry: false,
    gcTime: 0,
    refetchOnWindowFocus: 'always',
  });
  const plugin = catalogue.data?.find((x) => x.id === preview.data?.pluginId);
  const install = plugin?.installation;
  const confirm = useMutation({
    networkMode: 'always',
    mutationFn: () => api.plugins.confirmLink(linkId, proof, install!.id),
    onSuccess: () => {
      window.location.assign(preview.data!.returnUrl);
    },
  });
  return (
    <div className="mx-auto max-w-2xl p-4 sm:p-8">
      <PageHeader
        eyebrow="Verbindung bestätigen"
        title="Zwei Workspaces."
        titleAccent="Ihre Freigabe."
        description="Prüfen Sie beide Namen, bevor Sie die Verbindung bestätigen."
      />
      <div className="bg-card border-line shadow-card space-y-6 rounded-lg border p-6">
        <PluginProblem error={preview.error || catalogue.error || confirm.error} />
        {!linkId || !nonce || !state ? (
          <p>
            Der Verbindungslink ist unvollständig. Starten Sie die Verbindung erneut in GuestAI.
          </p>
        ) : preview.isPending ? (
          <p role="status">Verbindung wird geprüft…</p>
        ) : (
          preview.data && (
            <>
              <dl className="space-y-5">
                <div>
                  <dt className="text-ink-soft text-sm">StayOn-Workspace</dt>
                  <dd className="break-words font-serif text-2xl">
                    {preview.data.stayOnWorkspaceName ?? user?.workspaceName}
                  </dd>
                </div>
                <div>
                  <dt className="text-ink-soft text-sm">GuestAI-Workspace</dt>
                  <dd className="break-words font-serif text-2xl">
                    {preview.data.externalWorkspaceName}
                  </dd>
                </div>
              </dl>
              <p className="text-ink-soft text-sm">
                {preview.data.purpose === 'Installation'
                  ? 'Sie verbinden diese beiden Workspaces. Persönliche Rechte werden separat vergeben.'
                  : 'Sie verbinden ausschließlich Ihren eigenen StayOn-Benutzer mit Ihrem angemeldeten GuestAI-Benutzer.'}
              </p>
              {preview.data.confirmed || confirm.isSuccess ? (
                <Button asChild variant="brand" size="lg">
                  <a href={preview.data.returnUrl}>Verbindung in GuestAI abschließen</a>
                </Button>
              ) : install &&
                (preview.data.purpose === 'Member' || plugin?.canManage) &&
                ['Pending', 'Active'].includes(install.state) ? (
                <Button
                  type="button"
                  variant="brand"
                  size="lg"
                  loading={confirm.isPending}
                  onClick={() => confirm.mutate()}
                >
                  Verbindung bestätigen
                </Button>
              ) : (
                <p>
                  Das Plugin muss zuerst von einem Admin in diesem StayOn-Workspace eingerichtet und
                  freigegeben werden.
                </p>
              )}
            </>
          )
        )}
        <p className="text-ink-soft text-sm">
          Bei einem falschen Workspace brechen Sie hier ab und starten die Verbindung mit dem
          passenden Konto neu.
        </p>
      </div>
    </div>
  );
}
